import type { AppRoute } from './routing.types'

/**
 * Extracts route metadata for a given path from a flattened route tree.
 *
 * @param routes - Array of route definitions to search
 * @param targetPath - The path to find metadata for (e.g., '/collaborators')
 * @returns Route metadata if found, undefined otherwise
 */
export function extractRouteMetadataByPath(
  routes: AppRoute[],
  targetPath: string,
): AppRoute['meta'] | undefined {
  const normalizedPath = targetPath.startsWith('/') ? targetPath.slice(1) : targetPath

  function searchRoutes(
    routeList: AppRoute[],
    currentPath: string = '',
  ): AppRoute['meta'] | undefined {
    for (const route of routeList) {
      const fullPath = currentPath ? `${currentPath}/${route.path || ''}` : route.path || ''

      // Check if this route matches the target path
      if (fullPath === normalizedPath) {
        // If this route has children with an index route, return the index route's metadata
        if (route.children) {
          const indexRoute = route.children.find((child) => child.index)
          if (indexRoute) {
            return indexRoute.meta
          }
        }

        return route.meta
      }

      // If this is an index route and we're looking for the parent path
      if (route.index && currentPath === normalizedPath) {
        return route.meta
      }

      // Special case: if we're looking for a path and this route has children with index
      if (route.children && route.path === normalizedPath) {
        // Look for index route in children
        const indexRoute = route.children.find((child) => child.index)
        if (indexRoute) {
          return indexRoute.meta
        }
      }

      // Search in children
      if (route.children) {
        const childResult = searchRoutes(route.children, fullPath)
        if (childResult) {
          return childResult
        }
      }
    }

    return undefined
  }

  return searchRoutes(routes)
}
